import { Box, Button, Card, Grid, Typography } from '@material-ui/core'
import axios from 'axios'
import arrayMutators from 'final-form-arrays'
import * as HttpStatus from 'http-status-codes'
import { get } from 'lodash'
import { useState } from 'react'
import { Labeled, Loading, NumberInput, required, useGetOne, useNotify, useRedirect } from 'react-admin'
import { Form } from 'react-final-form'

import { captureExceptionIfProduction, getLoadStop } from 'helpers/utils'
import {
  DESTINATION,
  LABEL_DROPOFF_DATE,
  LABEL_ORIGIN_TO_DESTINATION,
  LABEL_PICKUP_DATE,
  ORIGIN,
} from '../../helpers/constants'
import { validateTwoDecimals } from '../../helpers/validators'
import {
  AssignedDriverReferenceInput,
  LabeledTypographyField,
  LocalizedStopDateTimeField,
  OriginDestinationField,
} from '../Fields'

const AutoBidForm = ({ match }: any) => {
  const redirectTo = useRedirect()
  const notify = useNotify()

  const [submittingAutoBid, setSubmittingAutoBid] = useState(false)
  const { data: load, loading, error } = useGetOne('loads', match.params.id)

  if (loading || submittingAutoBid) {
    return <Loading />
  }

  if (error) {
    return <p>Error</p>
  }

  if (!load) {
    return null
  }

  const submit = (values: any) => {
    setSubmittingAutoBid(true)
    const payload = {
      load_id: values.loadId,
      bid_minimum: values.lowest_rate,
      bid_maximum: values.preferred_rate,
      assigned_to: values.assigned_to,
    }
    axios
      .post('/api/v3/autobid/create-autobid-negotiation/', payload)
      .then((response) => {
        if (response.status === HttpStatus.CREATED) {
          setSubmittingAutoBid(false)
          notify('Auto-Bid Successfully Submitted!', { type: 'success' })
          const contactedLoadId = get(response, 'data.id')
          if (contactedLoadId) {
            redirectTo(`/contacted-loads/${contactedLoadId}/show`)
          } else {
            redirectTo('/contacted-loads')
          }
        } else {
          throw response
        }
      })
      .catch((error) => {
        setSubmittingAutoBid(false)
        notify(`${error.response.statusText}: ${JSON.stringify(error.response.data)}`, { type: 'warning' })
        captureExceptionIfProduction(error)
      })
  }

  var initialValues: any = {
    loadId: load.id,
  }

  const originStop = getLoadStop(load, ORIGIN)
  const destStop = getLoadStop(load, DESTINATION)

  return (
    <>
      <Box>
        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
          <Grid item xs={12}>
            <Typography component="span" variant="h5" color="textPrimary">
              Auto-bid for user
            </Typography>
          </Grid>
          <Grid container item direction="row">
            <Grid item xs={2}>
              <LabeledTypographyField label="Source" variant="body1" value={load.source} />
            </Grid>
            <Grid item xs={2}>
              <LabeledTypographyField label="Source ID" variant="body1" value={load.source_human_id} />
            </Grid>
            <Grid item xs>
              <LabeledTypographyField label="Load ID" variant="body1" value={load.id} />
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Card style={{ padding: 20, marginTop: 20 }}>
        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
          <Grid item xs={12}>
            <Labeled label={LABEL_ORIGIN_TO_DESTINATION}>
              <OriginDestinationField record={load} />
            </Labeled>
          </Grid>
          <Grid item xs={12}>
            <Labeled label={LABEL_PICKUP_DATE}>
              <LocalizedStopDateTimeField record={originStop} source="date_start" />
            </Labeled>
          </Grid>
          <Grid item xs={12}>
            <Labeled label={LABEL_DROPOFF_DATE}>
              <LocalizedStopDateTimeField record={destStop} source="date_start" />
            </Labeled>
          </Grid>
          <Grid item>
            <Box display="flex">
              <Form
                initialValues={initialValues}
                onSubmit={submit}
                mutators={{ ...arrayMutators }}
                subscription={defaultSubscription}
                keepDirtyOnReinitialize
                render={(formProps) => {
                  return (
                    <form>
                      <Box marginTop={1}>
                        <Box width={400}>
                          <AssignedDriverReferenceInput />
                          <Box>
                            <NumberInput
                              source="preferred_rate"
                              label="Preferred Rate"
                              validate={[required(), validateTwoDecimals]}
                              fullWidth
                            />
                          </Box>
                          <Box>
                            <NumberInput
                              source="lowest_rate"
                              label="Lowest Rate to Accept"
                              validate={[required(), validateTwoDecimals]}
                              fullWidth
                            />
                          </Box>
                        </Box>
                        <Box marginTop={2}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            onClick={formProps.handleSubmit}
                            disabled={formProps.submitting || formProps.invalid}
                          >
                            Submit auto-bid
                          </Button>
                        </Box>
                        <Box marginTop={3} width={400}>
                          <Typography variant="body2" style={{ fontStyle: 'italic' }}>
                            Once you submit this auto-bid, any broker offer that is within this bid range will
                            be automatically accepted and a job will be created.
                          </Typography>
                        </Box>
                      </Box>
                    </form>
                  )
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Card>
    </>
  )
}

const defaultSubscription = {
  submitting: true,
  pristine: true,
  valid: true,
  invalid: true,
  values: true,
}

export default AutoBidForm
